package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.dsl.ir.expr.StmtNode
import top.wcpe.allincore.dsl.ir.model.UiIr
import top.wcpe.allincore.dsl.ir.model.UiNode
import top.wcpe.allincore.dsl.ir.model.action.UiAction
import top.wcpe.allincore.dsl.ir.model.animation.UiAnimation
import top.wcpe.allincore.dsl.ir.model.statemachine.UiStateMachine
import top.wcpe.allincore.ui.runtime.animation.StateMachineInstance
import top.wcpe.allincore.ui.runtime.element.ModelPreviewInstanceKeys
import top.wcpe.allincore.ui.runtime.element.Slider
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.event.ChangeEvent
import top.wcpe.allincore.ui.runtime.event.DragEvent
import top.wcpe.allincore.ui.runtime.event.KeyEvent
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.ScrollEvent
import top.wcpe.allincore.ui.runtime.internal.concurrent.newConcurrentIdentityMap
import top.wcpe.allincore.ui.runtime.internal.concurrent.newConcurrentSet
import top.wcpe.allincore.ui.runtime.internal.concurrent.newIdentityMap
import top.wcpe.allincore.ui.runtime.ir.api.DisplayApi
import top.wcpe.allincore.ui.runtime.ir.api.KeyBindingHost
import top.wcpe.allincore.ui.runtime.ir.api.LocalApi
import top.wcpe.allincore.ui.runtime.ir.api.SlotApi
import top.wcpe.allincore.ui.runtime.ir.call.ActionDispatcher
import top.wcpe.allincore.ui.runtime.ir.eval.EvalContext
import top.wcpe.allincore.ui.runtime.ir.request.RequestDispatcher
import top.wcpe.allincore.ui.runtime.ir.request.RequestRegistry
import top.wcpe.allincore.ui.runtime.ir.subscribe.SubscribeRouter
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmField
import kotlin.jvm.JvmStatic

/**
 * IR 解释器：把服务端下发的 [UiIr] 实例化为 UIElement 树。
 *
 * 流程：
 * 1. 建立 state 信号表（默认值 → `Reactive.signal`）
 * 2. 反序列化 actions（lazy：首次触发时再读 bytes）
 * 3. 递归实例化节点：LITERAL 直接 set；EXPR 包进 [top.wcpe.allincore.ui.runtime.reactive.Effect]
 *    自动联动；EVENT 注册 onClick
 *
 * 仅依赖 reactive / element / layout-engine 公共 API。
 *
 * **架构（Phase2 拆分）**：本类已收缩为**薄门面**，零业务逻辑——
 * - public `@JvmStatic` 入口（interpret / interpretSession / instantiateOverlayRoot）纯转发到
 *   [IrSessionAssembler]（会话装配）；
 * - `*ToMap` 转发到 [EventMapBuilder]（事件 → Map）；
 * - 递归建树核心在 [ElementTreeBuilder]，数据驱动 diff 在 [DataDrivenDiffer]，
 *   `$cond` 重组在 [ConditionalRecomposer]，overlay 挂载在 [ElementOverlayAttacher]。
 *   循环互递归经依赖倒置（注入 `::buildElement`）打破。
 * - 嵌套类 [Context]（解释期共享上下文）留在门面，供 [EventBindingDispatcher] 等同包协作者引用。
 */
@Suppress("TooManyFunctions")
object IrInterpreter {

    /** 入口（兼容旧调用）：仅绑定 ActionDispatcher，不支持 request。 */
    @JvmStatic
    fun interpret(ir: UiIr, dispatcher: ActionDispatcher): UIElement<*> = IrSessionAssembler.interpret(ir, dispatcher)

    /** 入口：支持 request 的完整解释。 */
    @JvmStatic
    fun interpret(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
    ): UIElement<*> = IrSessionAssembler.interpret(ir, actionDispatcher, requestDispatcher, registry)

    /** 解释并返回完整 session（含 states/actions/evalContext，供外部驱动使用）。 */
    @JvmStatic
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
    ): IrSession = IrSessionAssembler.interpretSession(ir, actionDispatcher, requestDispatcher, registry)

    /** 解释并返回完整 session（增加 LocalApi 注入入口）。 */
    @JvmStatic
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
    ): IrSession = IrSessionAssembler.interpretSession(ir, actionDispatcher, requestDispatcher, registry, localApi)

    /** C6：解释并返回完整 session，可注入 DslLogger（print/log 输出端）。 */
    @JvmStatic
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
    ): IrSession = IrSessionAssembler.interpretSession(
        ir,
        actionDispatcher,
        requestDispatcher,
        registry,
        localApi,
        logger,
    )

    /** B4：解释并返回完整 session，注入 SlotCountResolver 以支持 dynamic inventory / slotGrid 运行时展开。 */
    @JvmStatic
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
    ): IrSession = IrSessionAssembler.interpretSession(
        ir,
        actionDispatcher,
        requestDispatcher,
        registry,
        localApi,
        logger,
        slotCountResolver,
    )

    /** 完整入口（含 DslExecutor）：平台层创建 session 时使用，DSL 逻辑在独立线程执行。 */
    @JvmStatic
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
    ): IrSession = IrSessionAssembler.interpretSession(
        ir,
        actionDispatcher,
        requestDispatcher,
        registry,
        localApi,
        logger,
        slotCountResolver,
        executor,
    )

    /** 完整入口（含 DslExecutor 与 SlotApi）：客户端接入响应式槽位读取时使用。 */
    @JvmStatic
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
        slotApi: SlotApi?,
    ): IrSession = IrSessionAssembler.interpretSession(
        ir, actionDispatcher, requestDispatcher, registry, localApi, logger, slotCountResolver, executor, slotApi,
    )

    /** B7：完整入口（含 SubscribeRouter）：支持订阅路由的 session 创建。 */
    @JvmStatic
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
        slotApi: SlotApi?,
        subscribeRouter: SubscribeRouter?,
    ): IrSession = IrSessionAssembler.interpretSession(
        ir, actionDispatcher, requestDispatcher, registry, localApi, logger,
        slotCountResolver, executor, slotApi, subscribeRouter,
    )

    /** Phase 4：完整入口（含 KeyBindingHost）：支持 HUD keybinding 的 session 创建。 */
    @JvmStatic
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
        slotApi: SlotApi?,
        subscribeRouter: SubscribeRouter?,
        keyBindingHost: KeyBindingHost,
    ): IrSession = IrSessionAssembler.interpretSession(
        ir, actionDispatcher, requestDispatcher, registry, localApi, logger,
        slotCountResolver, executor, slotApi, subscribeRouter, keyBindingHost,
    )

    /** Phase 4 + v2 params：支持 HUD keybinding 与只读打开参数的 session 创建。 */
    @JvmStatic
    @Suppress("LongParameterList")
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
        slotApi: SlotApi?,
        subscribeRouter: SubscribeRouter?,
        keyBindingHost: KeyBindingHost,
        params: Map<String, Any?>?,
    ): IrSession = IrSessionAssembler.interpretSession(
        ir, actionDispatcher, requestDispatcher, registry, localApi, logger,
        slotCountResolver, executor, slotApi, subscribeRouter, keyBindingHost, params = params,
    )

    /** 完整入口（含 DisplayApi）：客户端接入展示物品读取（display.read）时使用。 */
    @JvmStatic
    @Suppress("LongParameterList")
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
        slotApi: SlotApi?,
        subscribeRouter: SubscribeRouter?,
        keyBindingHost: KeyBindingHost,
        displayApi: DisplayApi?,
    ): IrSession = IrSessionAssembler.interpretSession(
        ir, actionDispatcher, requestDispatcher, registry, localApi, logger,
        slotCountResolver, executor, slotApi, subscribeRouter, keyBindingHost, displayApi,
    )

    /** 完整入口（含 DisplayApi 与 v2 params）：客户端同时接入展示物品读取与只读打开参数时使用。 */
    @JvmStatic
    @Suppress("LongParameterList")
    fun interpretSession(
        ir: UiIr,
        actionDispatcher: ActionDispatcher,
        requestDispatcher: RequestDispatcher,
        registry: RequestRegistry,
        localApi: LocalApi?,
        logger: DslLogger?,
        slotCountResolver: SlotCountResolver?,
        executor: DslExecutor?,
        slotApi: SlotApi?,
        subscribeRouter: SubscribeRouter?,
        keyBindingHost: KeyBindingHost,
        displayApi: DisplayApi?,
        params: Map<String, Any?>?,
    ): IrSession = IrSessionAssembler.interpretSession(
        ir, actionDispatcher, requestDispatcher, registry, localApi, logger,
        slotCountResolver, executor, slotApi, subscribeRouter, keyBindingHost, displayApi, params,
    )

    /** OverlayLayer 使用的最小子树实例化入口，共享调用方 EvalContext。 */
    @JvmStatic
    fun instantiateOverlayRoot(ir: UiIr?, evalCtx: EvalContext?): UIElement<*>? =
        IrSessionAssembler.instantiateOverlayRoot(ir, evalCtx)

    /** 把 KeyEvent 转为 DSL 可访问的 Map；委托 [EventMapBuilder]。 */
    internal fun keyEventToMap(event: KeyEvent): Map<String, Any?> = EventMapBuilder.keyEventToMap(event)

    /** 把 MouseEvent 转为 DSL 可访问的 Map；委托 [EventMapBuilder]。 */
    internal fun mouseEventToMap(event: MouseEvent): Map<String, Any?> = EventMapBuilder.mouseEventToMap(event)

    /** 把 ChangeEvent 转为 DSL 可访问的 Map；委托 [EventMapBuilder]。 */
    internal fun changeEventToMap(event: ChangeEvent<*>): Map<String, Any?> = EventMapBuilder.changeEventToMap(event)

    /** 把 DragEvent 转为 DSL 可访问的 Map；委托 [EventMapBuilder]。 */
    internal fun dragEventToMap(event: DragEvent): Map<String, Any?> = EventMapBuilder.dragEventToMap(event)

    /** 把 ScrollEvent 转为 DSL 可访问的 Map；委托 [EventMapBuilder]。 */
    internal fun scrollEventToMap(event: ScrollEvent): Map<String, Any?> = EventMapBuilder.scrollEventToMap(event)

    /**
     * 解释期上下文：state 信号、action 体、调度器、表达式求值上下文。
     *
     * 字段含义与并发约束（dataDrivenChildren / dataDrivenSignals / dataDrivenReactiveMaps /
     * conditionalState 均为 synchronized + IdentityHashMap，复合操作由 Effect 自身串行重入保证；
     * conditionalFailedIds 为 synchronizedSet）见各 @JvmField 注释及方法侧引用。
     *
     * 本类留在门面（不下沉到协作者）：是所有协作者共享的可变上下文类型，亦被
     * [EventBindingDispatcher] 以 `IrInterpreter.Context` 引用，保持 ABI 不变。
     */
    @Suppress("LongParameterList")
    internal class Context private constructor(
        @JvmField val screenId: String,
        @JvmField val states: Map<String, Signal<Any?>>,
        @JvmField val actions: Map<String, List<StmtNode>>,
        @JvmField val actionDefs: Map<String, UiAction>,
        @JvmField val animations: Map<String, UiAnimation>,
        @JvmField val stateMachines: Map<String, UiStateMachine>,
        @JvmField val dispatcher: ActionDispatcher,
        @JvmField val evalCtx: EvalContext,
        @JvmField val slotCountResolver: SlotCountResolver,
        /**
         * 循环槽 tooltip 修复:当前屏 IR 引用,供数据驱动重建路径
         * （[DataDrivenDiffer.diffListChildren] / [DataDrivenDiffer.instantiateTemplateChildren]）
         * 对实例化子树补挂 overlay 描述符。由 build 入口在构造后写入；派生 Context 共享同一引用。
         */
        @JvmField var ir: UiIr?,
        @JvmField val lastValues: MutableMap<UIElement<*>, MutableMap<String, Any?>>,
        @JvmField val fsmInstances: MutableMap<UIElement<*>, MutableMap<String, StateMachineInstance>>,
        /**
         * M6-c：数据驱动容器 → key → 当前已实例化 child；用于 diff 复用。
         *
         * 并发约束：source 求值通过 [top.wcpe.allincore.ui.runtime.reactive.Effect] 触发，可能从渲染主线程
         * 或 DslExecutor 单线程触发。即便 DSL 任务实际串行化，Effect 的失效再求值仍可能发生在订阅信号被
         * 外部线程更新时（`Signal.set` 在不同线程都允许）。因此本字段使用 [Collections.synchronizedMap]
         * 包装的 [IdentityHashMap]，仅保证 put/get/remove 单步原子；diff 内的 "读旧 → 算新 → 写回" 复合
         * 操作由 Effect 自身的串行重入保证（同一容器同时只有一个 Effect 在跑）。
         */
        @JvmField val dataDrivenChildren: MutableMap<UIElement<*>, MutableMap<Any?, UIElement<*>>>,
        /**
         * 数据驱动列表 diff 的模板形参 Signal 缓存：container → (key → 该条目所有形参的 Signal 列表)。
         * 并发模型与 dataDrivenChildren 一致。
         */
        @JvmField val dataDrivenSignals: MutableMap<UIElement<*>, MutableMap<Any?, MutableList<Signal<Any?>>>>,
        /**
         * 数据驱动列表 diff 的 ReactiveMap 字段引用缓存：container → (key → ReactiveMap)。
         * 并发模型与 dataDrivenChildren 一致。
         */
        @JvmField val dataDrivenReactiveMaps: MutableMap<UIElement<*>, MutableMap<Any?, Any?>>,
        /**
         * RFC-RECOMPOSITION：conditional 容器 → 当前命中分支状态（child + index）。
         * 并发模型与 dataDrivenChildren 一致。
         */
        @JvmField val conditionalState: MutableMap<UIElement<*>, ConditionalRecomposer.ConditionalState>,
        /**
         * RFC-RECOMPOSITION：已记录过首次失败堆栈的 conditional 节点 id 集合。
         * Set 是线程安全的（synchronizedSet），并发模型与 conditionalState 一致。
         */
        @JvmField val conditionalFailedIds: MutableSet<String>,
        @JvmField val sliderBindings: SliderBindingHandler,
        @JvmField val runtimeNodePath: String,
        @JvmField val runtimeDynamicKey: String,
    ) {

        /**
         * M6-c：派生一个仅 evalCtx 不同（用于绑定模板形参）的 Context；
         * 其余字段（states/actions/lastValues/dataDrivenChildren/sliderBindings 等）共享，确保 diff 状态对外可见。
         */
        fun withEvalCtx(newEvalCtx: EvalContext): Context = copyWith(evalContext = newEvalCtx)

        fun withRootNode(node: UiNode): Context = copyWith(
            nodePath = ModelPreviewInstanceKeys.rootPath(screenId, node.kind),
            dynamicKey = "",
        )

        fun withChildNode(index: Int, node: UiNode): Context = copyWith(
            nodePath = ModelPreviewInstanceKeys.childPath(runtimeNodePath, index, node.kind),
        )

        fun withBranchNode(index: Int, node: UiNode): Context = copyWith(
            nodePath = ModelPreviewInstanceKeys.branchPath(runtimeNodePath, index, node.kind),
        )

        fun withTemplateNode(index: Int, node: UiNode): Context = copyWith(
            nodePath = ModelPreviewInstanceKeys.templatePath(runtimeNodePath, index, node.kind),
        )

        fun withDynamicItem(key: Any?, index: Int): Context = copyWith(
            dynamicKey = ModelPreviewInstanceKeys.appendDynamicKey(runtimeDynamicKey, key, index),
        )

        private fun copyWith(
            evalContext: EvalContext = evalCtx,
            nodePath: String = runtimeNodePath,
            dynamicKey: String = runtimeDynamicKey,
        ): Context = Context(
            screenId,
            states,
            actions,
            actionDefs,
            animations,
            stateMachines,
            dispatcher,
            evalContext,
            slotCountResolver,
            ir,
            lastValues,
            fsmInstances,
            dataDrivenChildren,
            dataDrivenSignals,
            dataDrivenReactiveMaps,
            conditionalState,
            conditionalFailedIds,
            sliderBindings,
            nodePath,
            dynamicKey,
        )

        companion object {
            /**
             * 主入口工厂：分配各 Context 级映射 + 创建与 [lastValues] 绑定的 [SliderBindingHandler]，
             * 再委托全参私有构造器。等价于原 Java 的「9 参 public 构造器」。
             */
            @JvmStatic
            @Suppress("LongParameterList")
            fun of(
                screenId: String,
                states: Map<String, Signal<Any?>>,
                actions: Map<String, List<StmtNode>>,
                actionDefs: Map<String, UiAction>,
                animations: Map<String, UiAnimation>,
                stateMachines: Map<String, UiStateMachine>,
                dispatcher: ActionDispatcher,
                evalCtx: EvalContext,
                slotCountResolver: SlotCountResolver,
            ): Context {
                val lastValues: MutableMap<UIElement<*>, MutableMap<String, Any?>> = newIdentityMap()
                val sliderBindings = SliderBindingHandler(object : SliderBindingHandler.ValueMemo {
                    override fun recall(slider: Slider, key: String): Any? {
                        val values = lastValues[slider]
                        return values?.get(key)
                    }

                    override fun remember(slider: Slider, key: String, value: Any?) {
                        lastValues.getOrPut(slider) { LinkedHashMap() }[key] = value
                    }
                })
                return Context(
                    screenId,
                    states,
                    actions,
                    actionDefs,
                    animations,
                    stateMachines,
                    dispatcher,
                    evalCtx,
                    slotCountResolver,
                    null,
                    lastValues,
                    newIdentityMap(),
                    // dataDrivenChildren / dataDrivenSignals / dataDrivenReactiveMaps：synchronized + IdentityHashMap，
                    // 并发模型见字段注释——put/get/remove 单步原子，复合操作由 Effect 自身串行重入保证。
                    newConcurrentIdentityMap(),
                    newConcurrentIdentityMap(),
                    newConcurrentIdentityMap(),
                    // RFC-RECOMPOSITION：conditional 容器状态映射，并发模型与 dataDrivenChildren 一致
                    newConcurrentIdentityMap(),
                    newConcurrentSet(),
                    sliderBindings,
                    "",
                    "",
                )
            }
        }
    }
}
